import React, { useCallback, useEffect, useState } from "react";

import AIUsageDashboard from "./AIUsageDashboard";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "";

type AdminDashboardProps = {
  onLogout: () => void;
};

function getToken(): string | null {
  return localStorage.getItem("token");
}

type Teacher = {
  id: string;
  email: string;
  display_name?: string | null;
  role: string;
  is_active: boolean;
  teacher_id?: string | null;
  student_limit?: number | null;
};

export default function AdminDashboard({ onLogout }: AdminDashboardProps) {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [studentLimit, setStudentLimit] = useState("");
  const [accountType, setAccountType] = useState<"teacher" | "supervisor">("teacher");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadTeachers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();

      const endpoint =
        accountType === "supervisor"
          ? `${API_BASE}/users/supervisors`
          : `${API_BASE}/users/teachers`;

      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Could not load teachers.");
      }

      const data = await response.json();
      setTeachers(data);
    } catch (err: any) {
      setError(err?.message ?? "Could not load teachers.");
    } finally {
      setLoading(false);
    }
  }, [accountType]);

  useEffect(() => {
    loadTeachers();
  }, [loadTeachers]);

  const createTeacher = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setCreating(true);

    try {
      const token = getToken();

      const payload: any = {
        email,
        password,
        display_name: displayName || null,
      };

      if (studentLimit.trim()) {
        payload.student_limit = Number(studentLimit);
      }

      const endpoint =
        accountType === "supervisor"
          ? `${API_BASE}/users/supervisors`
          : `${API_BASE}/users/teachers`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail ?? "Could not create teacher.");
      }

      setDisplayName("");
      setEmail("");
      setPassword("");
      setStudentLimit("");
      setMessage(
        accountType === "supervisor"
          ? "Supervisor created."
          : "Teacher created."
      );
      await loadTeachers();
    } catch (err: any) {
      setError(err?.message ?? "Could not create teacher.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#050608",
        color: "#e5e5e5",
        padding: "1.5rem",
      }}
    >
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <div>
            <h1 style={{ margin: 0 }}>Admin Dashboard</h1>
            <p style={{ margin: "0.35rem 0 0", opacity: 0.75 }}>
              Monitor AI usage and manage organisational accounts.
            </p>
          </div>

          <button
            type="button"
            onClick={onLogout}
            style={{
              padding: "0.55rem 0.9rem",
              borderRadius: 999,
              border: "1px solid #6b7280",
              background: "transparent",
              color: "#e5e5e5",
              cursor: "pointer",
            }}
          >
            Log out
          </button>
        </header>

        {error && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.85rem",
              borderRadius: 12,
              border: "1px solid #7f1d1d",
              background: "#2a0d0d",
              color: "#fecaca",
            }}
          >
            {error}
          </div>
        )}

        {message && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "0.85rem",
              borderRadius: 12,
              border: "1px solid #14532d",
              background: "#062816",
              color: "#bbf7d0",
            }}
          >
            {message}
          </div>
        )}

        <AIUsageDashboard />

        <section
          style={{
            padding: "1rem",
            borderRadius: 14,
            border: "1px solid #222",
            background: "#111",
            marginBottom: "1rem",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Account administration</h2>
          <p style={{ opacity: 0.75 }}>
            Create and manage teacher or supervisor accounts separately from
            AI usage monitoring.
          </p>
          <h3>Create teacher or supervisor</h3>

          <form
            onSubmit={createTeacher}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 0.7fr auto",
              gap: "0.75rem",
              alignItems: "end",
            }}
          >

            <label>
              <div style={{ marginBottom: "0.35rem", opacity: 0.8 }}>
                Account type
              </div>

              <select
                value={accountType}
                onChange={(event) =>
                  setAccountType(
                    event.target.value as "teacher" | "supervisor"
                  )
                }
                style={inputStyle}
              >
                <option value="teacher">Teacher</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </label>

            <label>
              <div style={{ marginBottom: "0.35rem", opacity: 0.8 }}>Name</div>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="Teacher name"
                style={inputStyle}
              />
            </label>

            <label>
              <div style={{ marginBottom: "0.35rem", opacity: 0.8 }}>Email</div>
              <input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="teacher@example.com"
                type="email"
                required
                style={inputStyle}
              />
            </label>

            <label>
              <div style={{ marginBottom: "0.35rem", opacity: 0.8 }}>Password</div>
              <input
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Temporary password"
                type="password"
                required
                minLength={8}
                style={inputStyle}
              />
            </label>

            <label>
              <div style={{ marginBottom: "0.35rem", opacity: 0.8 }}>
                Student account limit
                </div>
              <input
                value={studentLimit}
                onChange={(event) => setStudentLimit(event.target.value)}
                placeholder="Optional"
                type="number"
                min={1}
                style={inputStyle}
              />
            </label>

            <button
              type="submit"
              disabled={creating}
              style={{
                border: "none",
                borderRadius: 999,
                padding: "0.8rem 1.2rem",
                background: "#0991ED",
                color: "white",
                fontWeight: 700,
                cursor: creating ? "not-allowed" : "pointer",
                opacity: creating ? 0.65 : 1,
              }}
            >
              {creating ? "Creating…" : "Create teacher"}
            </button>
          </form>
        </section>

        <section
          style={{
            padding: "1rem",
            borderRadius: 14,
            border: "1px solid #222",
            background: "#111",
          }}
        >
          <h2 style={{ marginTop: 0 }}>Teachers</h2>

          {loading ? (
            <p style={{ opacity: 0.75 }}>Loading teachers…</p>
          ) : teachers.length === 0 ? (
            <p style={{ opacity: 0.75 }}>No teachers yet.</p>
          ) : (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              {teachers.map((teacher) => (
                <div
                  key={teacher.id}
                  style={{
                    padding: "0.9rem",
                    borderRadius: 12,
                    border: "1px solid #24324a",
                    background: "#080b10",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "1rem",
                    alignItems: "start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>
                        {teacher.display_name || teacher.email}
                      </div>

                      <span
                        style={{
                          fontSize: "0.75rem",
                          padding: "0.2rem 0.5rem",
                          borderRadius: 999,
                          border: "1px solid #334155",
                          opacity: 0.8,
                        }}
                      >
                        {teacher.role}
                      </span>
                    </div>
                    <div style={{ opacity: 0.65, marginTop: "0.2rem" }}>
                      {teacher.email}
                    </div>
                    <div style={{ opacity: 0.65, marginTop: "0.5rem" }}>
                      Student limit:{" "}
                      <strong style={{ color: "#e5e5e5" }}>
                        {teacher.student_limit ?? "No limit set"}
                      </strong>
                    </div>
                  </div>

                  <span
                    style={{
                      border: "1px solid #374151",
                      borderRadius: 999,
                      padding: "0.35rem 0.65rem",
                      fontSize: "0.85rem",
                      color: teacher.is_active ? "#bbf7d0" : "#fecaca",
                    }}
                  >
                    {teacher.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "0.75rem",
  borderRadius: 8,
  border: "1px solid #334155",
  background: "#06080c",
  color: "#e5e5e5",
};
